import * as React from 'react';

import { cn } from '../lib/utils';

/**
 * Skip-link primitive — WCAG 2.4.1 (Bypass Blocks).
 *
 * A keyboard-first anchor that lets sighted assistive-tech users jump past the
 * sidebar/top-bar landmarks straight to the main content. Visually hidden by
 * default; revealed when keyboard-focused.
 *
 * The host application renders this as the very first child of `<body>`,
 * pointing to the `id` of the main landmark (`#main` by convention). The
 * matching CSS rule lives in the shell's global stylesheet under the
 * `.skip-link` class, so this primitive just provides the JSX shape and the
 * default copy.
 *
 * @example
 * <SkipLink href="#main">Skip to main content</SkipLink>
 */
export interface SkipLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Anchor target — usually `#main`. */
  href?: string;
}

export const SkipLink = React.forwardRef<HTMLAnchorElement, SkipLinkProps>(
  ({ href = '#main', className, children, ...props }, ref) => (
    <a
      ref={ref}
      href={href}
      className={cn('skip-link', className)}
      data-slot="skip-link"
      {...props}
    >
      {children ?? 'Skip to main content'}
    </a>
  )
);
SkipLink.displayName = 'SkipLink';
